import { useEffect, useState } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiX } from 'react-icons/fi';
import { getProducts, createProduct, updateProduct, deleteProduct } from '../../services/api';
import ImageUploader from '../../components/common/ImageUploader';
import toast from 'react-hot-toast';

const empty = {
  name: '', description: '', price: '', salePrice: '', category: 'shirts',
  ageGroup: 'adult', gender: 'unisex', fabric: '', isFeatured: false,
  images: [''], tags: '',
  sizes: [{ size: 'S', stock: 10 }, { size: 'M', stock: 10 }, { size: 'L', stock: 10 }],
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(empty);
  const [saving, setSaving]     = useState(false);

  const load = () => {
    setLoading(true);
    getProducts({ limit: 50 }).then(r => setProducts(r.data.products)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(empty); setEditing(null); setShowForm(true); };
  const openEdit   = (p)  => {
    setForm({ ...p, tags: p.tags?.join(', ') || '', images: p.images?.length ? p.images : [''], salePrice: p.salePrice || '' });
    setEditing(p._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this product?')) return;
    await deleteProduct(id);
    toast.success('Product removed');
    load();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        price:     Number(form.price),
        salePrice: form.salePrice ? Number(form.salePrice) : undefined,
        tags:      form.tags.split(',').map(t => t.trim()).filter(Boolean),
        images:    form.images.filter(Boolean),
      };
      if (editing) {
        await updateProduct(editing, payload);
        toast.success('Product updated');
      } else {
        await createProduct(payload);
        toast.success('Product created');
      }
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const f = (key) => ({
    value: form[key] ?? '',
    onChange: e => setForm(p => ({ ...p, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value })),
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-4xl font-bold text-yamass-black">Products</h1>
        <button onClick={openCreate} className="flex items-center gap-2 bg-yamass-black text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-yamass-accent transition-colors">
          <FiPlus size={16} /> Add Product
        </button>
      </div>

      {/* Product table */}
      {loading ? (
        <div className="space-y-3">{[...Array(6)].map((_, i) => <div key={i} className="skeleton h-16 rounded-xl" />)}</div>
      ) : (
        <div className="bg-yamass-cream rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-yamass-black text-white">
                <th className="text-left px-5 py-4 font-medium">Product</th>
                <th className="text-left px-5 py-4 font-medium hidden md:table-cell">Category</th>
                <th className="text-left px-5 py-4 font-medium">Price</th>
                <th className="text-left px-5 py-4 font-medium hidden md:table-cell">Featured</th>
                <th className="px-5 py-4" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {products.map(p => (
                <tr key={p._id} className="hover:bg-white/50 transition-colors">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg overflow-hidden bg-white flex-shrink-0">
                        {p.images?.[0] ? <img src={p.images[0]} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-lg">👕</div>}
                      </div>
                      <span className="font-medium text-yamass-black line-clamp-1">{p.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-gray-500 capitalize hidden md:table-cell">{p.category}</td>
                  <td className="px-5 py-4 font-semibold text-yamass-black">
                    {p.salePrice ? <><span className="text-yamass-accent">৳{p.salePrice}</span> <span className="text-gray-300 line-through text-xs">৳{p.price}</span></> : `৳${p.price}`}
                  </td>
                  <td className="px-5 py-4 hidden md:table-cell">
                    <span className={`text-xs px-2 py-1 rounded-full ${p.isFeatured ? 'bg-yamass-accent/10 text-yamass-accent' : 'bg-gray-100 text-gray-400'}`}>
                      {p.isFeatured ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2 justify-end">
                      <button onClick={() => openEdit(p)} className="p-2 text-gray-400 hover:text-yamass-black rounded-lg hover:bg-white transition-colors"><FiEdit2 size={15} /></button>
                      <button onClick={() => handleDelete(p._id)} className="p-2 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors"><FiTrash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {products.length === 0 && <p className="text-center py-12 text-gray-400">No products yet. Add your first product!</p>}
        </div>
      )}

      {/* Product form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-8 pt-8 pb-4 sticky top-0 bg-white border-b border-gray-100">
              <h2 className="font-display text-2xl font-bold text-yamass-black">{editing ? 'Edit Product' : 'New Product'}</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-yamass-black transition-colors"><FiX size={22} /></button>
            </div>
            <form onSubmit={handleSubmit} className="px-8 py-6 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Product Name *</label>
                  <input required {...f('name')} className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-yamass-accent" />
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Description *</label>
                  <textarea required rows={3} {...f('description')} className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-yamass-accent resize-none" />
                </div>
                {[
                  { key: 'price', label: 'Price (৳) *', required: true },
                  { key: 'salePrice', label: 'Sale Price (৳)', required: false },
                ].map(fi => (
                  <div key={fi.key}>
                    <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">{fi.label}</label>
                    <input type="number" required={fi.required} {...f(fi.key)} className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-yamass-accent" />
                  </div>
                ))}
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Category *</label>
                  <select {...f('category')} className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-yamass-accent">
                    {['shirts','pants','baby-clothes','kids-clothes','teen-clothes'].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Age Group</label>
                  <select {...f('ageGroup')} className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-yamass-accent">
                    {['infant','toddler','kids','teen','adult'].map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Gender</label>
                  <select {...f('gender')} className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-yamass-accent">
                    {['male','female','unisex'].map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Fabric</label>
                  <input {...f('fabric')} placeholder="e.g. 100% Cotton" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-yamass-accent" />
                </div>
                <div className="col-span-2">
                  <ImageUploader
                    images={form.images.filter(Boolean)}
                    onChange={(images) => setForm(p => ({ ...p, images }))}
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Or paste image URL</label>
                  <input
                    value={form.images[0] || ''}
                    onChange={e => setForm(p => ({ ...p, images: e.target.value ? [e.target.value] : [] }))}
                    placeholder="https://..."
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-yamass-accent"
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Tags (comma separated)</label>
                  <input {...f('tags')} placeholder="casual, summer, cotton" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-yamass-accent" />
                </div>
                <div className="col-span-2 flex items-center gap-3">
                  <input type="checkbox" id="featured" checked={form.isFeatured} onChange={e => setForm(p => ({ ...p, isFeatured: e.target.checked }))} className="w-4 h-4 accent-yamass-accent" />
                  <label htmlFor="featured" className="text-sm text-gray-600">Feature this product on homepage</label>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 border border-gray-200 text-gray-600 py-3 rounded-full text-sm font-medium hover:bg-gray-50 transition-colors">Cancel</button>
                <button type="submit" disabled={saving} className="flex-1 bg-yamass-black text-white py-3 rounded-full text-sm font-semibold hover:bg-yamass-accent transition-colors disabled:opacity-50">
                  {saving ? 'Saving...' : editing ? 'Update Product' : 'Create Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
